// Inspired by: https://fig.io
'use client';

import { c as _c } from "react/compiler-runtime";
import { SiGithub, SiWakatime, SiYoutube } from '@icons-pack/react-simple-icons';
import { ArrowRightIcon, PencilIcon, StarIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Counter } from '@/components/counter';
import { Link } from '@/components/ui/link';
import { useGithubStats, useLikeStats, useViewStats, useWakatimeStats, useYoutubeStats } from '@/hooks/queries/stats.query';
type Card = {
  icon: React.ReactNode;
  title: string;
  link: string;
  value: number | undefined;
  linkText: string;
  gradient: {
    startColor: string;
    endColor: string;
  };
  suffix?: string;
};
export function Stats() {
  const $ = _c(59);
  const youtubeQuery = useYoutubeStats();
  const githubQuery = useGithubStats();
  const likesQuery = useLikeStats();
  const viewsQuery = useViewStats();
  const wakatimeQuery = useWakatimeStats();
  const t = useTranslations();
  let t0;
  if ($[0] !== t) {
    t0 = t("dashboard.stat.coding-hours");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = wakatimeQuery.data?.hours;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SiWakatime className="text-[#0061ff]" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      startColor: "#0061ff",
      endColor: "#6f7bf7"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t0 || $[5] !== t1) {
    t4 = {
      title: t0,
      link: "https://wakatime.com/@braviaprime",
      value: t1,
      icon: t2,
      linkText: "WakaTime",
      gradient: t3,
      suffix: "hrs"
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("dashboard.stat.youtube-subscribers");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = youtubeQuery.data?.subscribers;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <SiYoutube className="text-[#ff0000]" />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      startColor: "#ff0000",
      endColor: "#ca1a1a"
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t5 || $[12] !== t6) {
    t9 = {
      title: t5,
      link: "https://www.youtube.com/@braviaprime",
      value: t6,
      icon: t7,
      linkText: "YouTube",
      gradient: t8
    };
    $[11] = t5;
    $[12] = t6;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t) {
    t10 = t("dashboard.stat.youtube-views");
    $[14] = t;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = youtubeQuery.data?.views;
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SiYoutube className="text-[#ff0000]" />;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      startColor: "#ff0000",
      endColor: "#ca1a1a"
    };
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t10 || $[19] !== t11) {
    t14 = {
      title: t10,
      link: "https://www.youtube.com/@braviaprime",
      value: t11,
      icon: t12,
      linkText: "YouTube",
      gradient: t13
    };
    $[18] = t10;
    $[19] = t11;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t) {
    t15 = t("dashboard.stat.github-followers");
    $[21] = t;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  const t16 = githubQuery.data?.followers;
  let t17;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <SiGithub className="text-[#fee000]" />;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      startColor: "#fee000",
      endColor: "#ffce63"
    };
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== t15 || $[26] !== t16) {
    t19 = {
      title: t15,
      link: "https://github.com/braviaprime",
      value: t16,
      icon: t17,
      linkText: "GitHub",
      gradient: t18
    };
    $[25] = t15;
    $[26] = t16;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== t) {
    t20 = t("dashboard.stat.github-stars");
    $[28] = t;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  const t21 = githubQuery.data?.stars;
  let t22;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <StarIcon className="size-6 text-[#fee000]" />;
    $[30] = t22;
  } else {
    t22 = $[30];
  }
  let t23;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      startColor: "#fee000",
      endColor: "#ffce63"
    };
    $[31] = t23;
  } else {
    t23 = $[31];
  }
  let t24;
  if ($[32] !== t20 || $[33] !== t21) {
    t24 = {
      title: t20,
      link: "https://github.com/braviaprime",
      value: t21,
      icon: t22,
      linkText: "GitHub",
      gradient: t23
    };
    $[32] = t20;
    $[33] = t21;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== t) {
    t25 = t("dashboard.stat.blog-total-views");
    $[35] = t;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  const t26 = viewsQuery.data?.views;
  let t27;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <PencilIcon className="size-6 text-[#ff0f7b]" />;
    $[37] = t27;
  } else {
    t27 = $[37];
  }
  let t28;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      startColor: "#ff0f7b",
      endColor: "#f945ff"
    };
    $[38] = t28;
  } else {
    t28 = $[38];
  }
  let t29;
  if ($[39] !== t25 || $[40] !== t26) {
    t29 = {
      title: t25,
      link: "https://braviaprime.com",
      value: t26,
      icon: t27,
      linkText: "Blog",
      gradient: t28
    };
    $[39] = t25;
    $[40] = t26;
    $[41] = t29;
  } else {
    t29 = $[41];
  }
  let t30;
  if ($[42] !== t) {
    t30 = t("dashboard.stat.blog-total-likes");
    $[42] = t;
    $[43] = t30;
  } else {
    t30 = $[43];
  }
  const t31 = likesQuery.data?.likes;
  let t32;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <PencilIcon className="size-6 text-[#ff0f7b]" />;
    $[44] = t32;
  } else {
    t32 = $[44];
  }
  let t33;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      startColor: "#ff0f7b",
      endColor: "#f945ff"
    };
    $[45] = t33;
  } else {
    t33 = $[45];
  }
  let t34;
  if ($[46] !== t30 || $[47] !== t31) {
    t34 = {
      title: t30,
      link: "https://braviaprime.com",
      value: t31,
      icon: t32,
      linkText: "Blog",
      gradient: t33
    };
    $[46] = t30;
    $[47] = t31;
    $[48] = t34;
  } else {
    t34 = $[48];
  }
  let t35;
  if ($[49] !== t14 || $[50] !== t19 || $[51] !== t24 || $[52] !== t29 || $[53] !== t34 || $[54] !== t4 || $[55] !== t9) {
    t35 = [t4, t9, t14, t19, t24, t29, t34];
    $[49] = t14;
    $[50] = t19;
    $[51] = t24;
    $[52] = t29;
    $[53] = t34;
    $[54] = t4;
    $[55] = t9;
    $[56] = t35;
  } else {
    t35 = $[56];
  }
  const data = t35;
  let t36;
  if ($[57] !== data) {
    t36 = <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">{data.map(_temp)}</div>;
    $[57] = data;
    $[58] = t36;
  } else {
    t36 = $[58];
  }
  return t36;
}
function _temp(stat) {
  const {
    icon,
    link,
    title,
    value,
    linkText,
    gradient: t0,
    suffix
  } = stat;
  const {
    startColor,
    endColor
  } = t0;
  const hasValue = value === 0 || value !== undefined;
  return <Link key={stat.title} href={link} className="group relative overflow-hidden rounded-xl border p-4 transition-colors hover:bg-accent"><div className="flex flex-col items-center justify-center gap-2 transition-transform group-hover:-translate-y-24 group-focus:-translate-y-24"><div className="flex items-center gap-2 text-3xl font-semibold">{hasValue ? <><span>{icon}</span><div style={{
            background: `linear-gradient(122.25deg, ${startColor} 12.16%, ${endColor} 70.98%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent"
          }}><Counter value={value} />{suffix && <span>{` ${suffix}`}</span>}</div></> : "--"}</div><div className="text-xl font-semibold">{title}</div></div><span className="absolute top-1/2 left-1/2 flex -translate-x-1/2 translate-y-24 items-center gap-1 text-2xl font-semibold opacity-0 transition group-hover:-translate-y-1/2 group-hover:opacity-100 group-focus:-translate-y-1/2 group-focus:opacity-100">{linkText}<ArrowRightIcon className="size-6" /></span></Link>;
}